import axios from "axios";
import api, { performanceApi } from "../api";
import jwtDecode from "jwt-decode";
import dayjs from "dayjs";

// Get user profile from localStorage for authentication
export let apiToken = JSON.parse(localStorage.getItem("user_profile"));

// Function to handle logout
const handleLogout = () => {
  localStorage.removeItem("user_profile");
  localStorage.removeItem("user");
  window.location.replace("/");
};

// Create core API instance with proper authentication
const createCoreApiInstance = () => {
  const instance = axios.create({
    baseURL: process.env.REACT_APP_CORE_URL,
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + apiToken?.accessToken,
    },
  });

  // Add request interceptor for token refresh
  instance.interceptors.request.use(
    async (req) => {
      if (!apiToken) {
        apiToken = localStorage.getItem("user_profile")
          ? JSON.parse(localStorage.getItem("user_profile"))
          : null;
        req.headers.Authorization = `Bearer ${apiToken?.accessToken}`;
      }

      if (apiToken?.accessToken) {
        const user = jwtDecode(apiToken?.accessToken);
        const isExpired = dayjs.unix(user?.exp).diff(dayjs()) < 1;

        if (isExpired) {
          handleLogout();
          return Promise.reject(new Error("Token expired"));
        }
      }

      return req;
    },
    (error) => Promise.reject(error)
  );

  // Add response interceptor for 401 errors
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response && error.response.status === 401) {
        handleLogout();
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

const core_api = createCoreApiInstance();

const PerformanceModule = () => {
  const userData = JSON.parse(localStorage.getItem("user_profile"));

  return {
    // https://analytics-dev.flexmonie.com/api/Transaction/GetTransactionsGroupedByTypeAndActorForSuperDealer?superDealerWalletId=111
    GetCoreReports: function (data) {
      const { superDealerWalletId, ...rest } = data;
      return performanceApi.get(
        `/Transaction/GetTransactionsGroupedByTypeAndActorForSuperDealer?superDealerWalletId=${superDealerWalletId}`,
        { params: rest }
      );
    },

    AgentPerformanceList: function (data) {
      // /Transaction/superDealer-agent-performance
      return performanceApi.get(`/Transaction/superDealer-agent-performance`, {
        params: data,
      });
    },

    AggregatorPerformanceList: function (data) {
      // /Transaction/superDealer-aggregator-performance
      return performanceApi.get(`/Transaction/superDealer-aggregator-performance`, {
        params: data,
      });
    },

    IndividualPerformanceList: function (data) {
      // /Transaction/superDealer-individual-performance
      return performanceApi.get(`/Transaction/superDealer-individual-performance`, {
        params: data,
      });
    },

    CorporatePerformanceList: function (data) {
      // /Transaction/superDealer-corporate-performance
      return performanceApi.get(`/Transaction/superDealer-corporate-performance`, {
        params: data,
      });
    },

    AgentProductPerformanceList: function (data) {
      const { date, product, customer_id } = data;
      return core_api.get(
        `/transactions/report/superdealer/performance/agent-products?customer_id=${customer_id}&service_codes=${product}&date=${date}`,
        data
      );
    },

    // ***************************************************************************************************

    GetPerformanceGraph: function (data) {
      const { date, product } = data;
      return core_api.get(
        `/transactions/report/superdealer/performance/overview?customer_id=${userData?.walletId}&service_codes=${product}&date=${date}`,
        data
      );
    },

    ListTransaction: function (data) {
      const { page, per_page, keyword, startDate, endDate } = data;
      // page=1&per_page=10&customer_id=501&keyword=2783
      return core_api.get(
        `/transactions/report/listings-of-agent-and-downline-v2?customer_id=${userData?.walletId}&page=${page}&per_page=${per_page}&keyword=${keyword}&StartDate=${startDate}&EndDate=${endDate}`,
        data
      );
    },

    CusstomerListing: function (data) {
      const { page, per_page, keyword } = data;
      // page=1&per_page=10&customer_id=501&keyword=2783
      return core_api.get(
        `/report/agent/agent-downline-listings-v2?customer_id=${userData?.walletId}&page=${page}&per_page=${per_page}&keyword=${keyword}`,
        data
      );
    },

    AgentProductPerformanceList: function (data) {
      const { date, product, customer_id } = data;
      return core_api.get(
        `/transactions/report/superdealer/performance/agent-products?customer_id=${customer_id}&service_codes=${product}&date=${date}`,
        data
      );
    },

    AgentPerformanceListFromReseller: function (data) {
      const { date, product, page, per_page, customer_id } = data;
      return core_api.get(
        `/transactions/report/superdealer/performance/top-agents?customer_id=${customer_id}&service_codes=${product}&date=${date}&page=${page}&per_page=${per_page}`,
        data
      );
    },

    ResellerPerformanceList: function (data) {
      const { date, product, page, per_page } = data;
      return core_api.get(
        `/transactions/report/superdealer/performance/top-resselers?customer_id=${userData?.walletId}&service_codes=${product}&date=${date}&page=${page}&per_page=${per_page}`,
        data
      );
    },
  };
};

export const PerformanceService = {
  performanceModule: PerformanceModule,
};
